import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildProviderPatientPath } from "@/routes";
import { format } from "date-fns";
import {
  Users,
  Search,
  User,
  Phone,
  Mail,
  Calendar,
  ChevronRight,
  FileText,
  Filter,
  Clipboard,
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export default function MyPatients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: patients, isLoading } = useQuery({
    queryKey: ["my-assigned-patients", user?.id],
    queryFn: async () => {
      if (!user) return [];
      // Get assigned member IDs
      const { data: assignments, error: assignError } = await supabase
        .from("provider_members" as any)
        .select("member_id")
        .eq("provider_id", user.id);
      if (assignError) throw assignError;
      const memberIds = (assignments || []).map((a: any) => a.member_id);
      if (memberIds.length === 0) return [];

      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .in("user_id", memberIds)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const filtered = patients?.filter((p) => {
    const matchesSearch =
      !search ||
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      p.phone?.includes(search) ||
      p.email?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === "all" || p.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusVariant = (status: string | null) => {
    switch (status) {
      case "completed":
        return "default";
      case "pending":
        return "secondary";
      case "reviewed":
        return "outline";
      default:
        return "secondary";
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <ProviderSidebar />
      <div className="flex-1 flex flex-col ml-64">
        <ProviderHeader />
        <main className="flex-1 p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
                  <Users className="h-8 w-8 text-primary" />
                  My Patients
                </h1>
                <p className="mt-1 text-muted-foreground">
                  All patients with submitted intake forms
                </p>
              </div>
              <Button onClick={() => navigate(ROUTES.PROVIDER_INTAKE)}>
                <FileText className="h-4 w-4 mr-2" />
                New Patient Intake
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, phone, or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Patients</p>
                <p className="text-2xl font-bold">{patients?.length || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">
                  {patients?.filter((p) => p.status === "pending").length || 0}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">
                  {patients?.filter((p) => p.status === "completed").length || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Patient List */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : filtered && filtered.length > 0 ? (
            <div className="space-y-3">
              {filtered.map((patient) => {
                const initials = `${patient.first_name?.[0] || ""}${patient.last_name?.[0] || ""}`.toUpperCase();
                const age = patient.date_of_birth
                  ? Math.floor(
                      (new Date().getTime() - new Date(patient.date_of_birth).getTime()) /
                        31557600000
                    )
                  : null;

                return (
                  <Card
                    key={patient.id}
                    className="cursor-pointer transition-all hover:shadow-md hover:border-primary/30"
                    onClick={() => navigate(buildProviderPatientPath(patient.user_id))}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={patient.patient_photo_url || undefined} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                            {initials}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground">
                              {patient.first_name} {patient.last_name}
                            </h3>
                            {age && (
                              <span className="text-sm text-muted-foreground">
                                {age} yrs
                              </span>
                            )}
                            <Badge variant={getStatusVariant(patient.status)} className="capitalize">
                              {patient.status || "unknown"}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {patient.insurance_type?.replace("_", " ")}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {patient.phone}
                            </span>
                            {patient.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {patient.email}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Submitted {format(new Date(patient.created_at), "MMM d, yyyy")}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(buildProviderPatientPath(patient.user_id) + '?tab=notes');
                            }}
                          >
                            <Clipboard className="h-4 w-4 mr-1" />
                            Notes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(buildProviderPatientPath(patient.user_id));
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-1">No patients found</h3>
                  <p className="text-muted-foreground">
                    {search || statusFilter !== "all"
                      ? "No patients match your search criteria. Try adjusting your filters."
                      : "Patients will appear here once they submit their intake forms."}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}
